import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AddBatchPayload,
  AddBatchGSheetLinkPayload,
  APIResponse,
  GetBatchesDropdownContent,
  GetBatchesForParentResponse,
  GetBatchesResponse,
} from "@src/pages/batches/batch-base-component/data.type";
import {
  GetBatchByIdResponse,
  QuizResponse,
  StudentAssessmentResponse,
  StudentAssessmentResult,
  StudentListforBatchResponse,
} from "@src/pages/batches/batches-quiz-details/BatchesQuizDetailsTypes";
import { GetBatchForStudentsResponse } from "@src/pages/batches/student-batch-list/data";
import { GetAllStudentForBatchResponse } from "@src/pages/batches/student-list-drawer/data.type";
import { InviteResponseData } from "@src/pages/task-manager/quiz/invite-people-drawer/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { FileResponse } from "./TournamentsApiSlice";

export interface VideoAccessPayload {
  allowForward: boolean;
  allowReplay: boolean;
  allowPlayBackSpeed: boolean;
  assignVideoToStudents: boolean;
}

export interface ResourceAccessPayload {
  batchId: string;
  granteeType: string;
  granteeId: string | string[];
  resourceType: string;
  resourceId: string;
  feature: string[];
  orderOfFeatures: string[];
  accessType: string;
  videoAccess: VideoAccessPayload;
  assessmentMandatory: boolean;
  assignmentMandatory: boolean;
  videoMandatory: boolean;
  practiceMandatory: boolean;
}

export interface ResourceAccessResponse {
  id: string | null;
  batchId: string;
  granteeType: string;
  granteeId: string | string[];
  resourceType: string;
  resourceId: string;
  feature: string[] | null;
  orderOfFeatures: string[] | null;
  accessType: string | null;
  grantedBy: string | null;
  active: boolean | null;
  videoAccess: VideoAccessPayload | null;
  assessmentMandatory: boolean | null;
  assignmentMandatory: boolean | null;
  videoMandatory: boolean | null;
  practiceMandatory: boolean | null;
}

export interface StudentResourceMetaData {
  assessmentComplete?: boolean;
  assignmentComplete?: boolean;
  practiceQuestionComplete?: boolean;
  assignmentCompleted?: boolean;
  assessmentCompleted?: boolean;
  practiceCompleted?: boolean;
  [key: string]: any;
}

export interface StudentResourceMetaDataResponse extends APIResponse {
  response: StudentResourceMetaData;
}

export interface BatchesDropdown {
  parentId: string;
  organizationType?: string;
}

interface GetAllBAtchesResponse extends APIResponse {
  response: GetBatchesResponse;
}

interface GetBatchesForOrg extends APIResponse {
  response: GetBatchesForParentResponse;
}

interface GetBatchesDropdownResponse extends APIResponse {
  response: GetBatchesDropdownContent[];
}

export interface StudentSummary {
  count: number;
  totalMarks: number;
  name: string;
  email: string;
}

export interface BatchStudentsElpReportResponse {
  studentEvaluationSummary: Record<string, StudentSummary>;
  totalQuestions: number;
  totalMarks: number;
}

export interface GetBatchStudentsElpReportApiResponse {
  httpStatus: string;
  statusCode: number;
  response: BatchStudentsElpReportResponse;
}

export interface SyllabusSubTopicWeight {
  subTopicId: string;
  weight: number;
  syllabusSubTopic?: SyllabusSubTopicWeight[];
}

export interface SyllabusTopicWeight {
  topicId: string;
  weight: number;
  syllabusSubTopic?: SyllabusSubTopicWeight[];
}

export interface SyllabusChapterWeight {
  chapterId: string;
  weight: number;
  syllabusTopic?: SyllabusTopicWeight[];
}

export interface SyllabusModuleWeight {
  moduleId: string;
  weight: number;
  syllabusChapters?: SyllabusChapterWeight[];
}

export interface SyllabusSubjectWeight {
  subjectId: string;
  weight: number;
  syllabusModules?: SyllabusModuleWeight[];
  syllabusChapters?: SyllabusChapterWeight[];
}

export interface UpdateBatchPayload {
  syllabusReference?: string;
  isTaskRoom?: boolean;
  trainerReference?: string;
  modeOfClass?: string;
  startDateAndTime?: string;
  endDate?: string;
  autoAssign?: boolean;
  expiryTime?: {
    days: number | null;
    hours: number | null;
  } | null;
  parentId?: string;
  parentType?: string;
  syllabusName?: string;
  subjectRef?: string;
  syllabusSubjects?: SyllabusSubjectWeight[];
  name?: string;
  qspBatchCode?: string;
  attendanceAllowed?: boolean;
  classRoomAllowed?: boolean;
  isPublic?: boolean;
  clientReference?: string;
  externalLink?: string;
  batchType?: string;
  batchCode?: string;
}

export interface UpdateBatchResponseData {
  createdOn: string;
  lastUpdateOn: string;
  createdByName: string;
  createdByEmail: string;
  modifiedByName: string;
  modifiedByEmail: string;
  id: string;
  syllabusReference: string;
  parentType: string;
  parentId: string;
  modeOfClass: string;
  startDateAndTime: string;
  trainerReference: string;
  isTaskRoom: boolean;
  studentReference: string[];
  batchCode: string;
  autoAssign: boolean;
  expiryTime: {
    days: number;
    hours: number;
  };
  subjectRef: string;
  syllabusTemplate: string;
  name: string;
  qspBatchCode: string;
  endDate: string;
  attendanceAllowed: boolean;
  classRoomAllowed: boolean;
  isPublic: boolean;
  clientReference: string;
  externalLink: string;
  batchType: string;
  classStarted: boolean;
}

export interface UpdateBatchApiResponse extends APIResponse {
  response: UpdateBatchResponseData;
}

export interface InviteResponse extends APIResponse {
  response: InviteResponseData;
}

export interface GetNotAssessmentNotAttendedStudentsResponse extends APIResponse {
  response: { id: string; name: string; email: string }[];
}

export interface StudentTreeResourceDataItem {
  resourceId: string;
  parentType: string | null;
  totalDuration: number | null;
  completedDuration: number | null;
  status: boolean | null;
  studentId: string;
  batchRef: string | null;
  totalAssessments: number;
  completedAssessments: number;
  totalAssignments: number;
  completedAssignments: number;
  resourceName: string | null;
}

export interface GetStudentTreeResourceDataResponse extends APIResponse {
  response: StudentTreeResourceDataItem[];
}

export interface AssignmentResultQuery {
  batchId: string;
  parentId: string;
  parentType: string;
  studentId: string;
}

export interface AssignmentResultResponse extends APIResponse {
  response: unknown;
}

export interface BatchTreeResourceDataItem {
  resourceId: string;
  parentType: string | null;
  batchRef: string | null;
  totalAssessments: number;
  totalAssignments: number;
  resourceName: string | null;
  totalVideos: number;
}

export interface GetBatchTreeResourceDataResponse extends APIResponse {
  response: BatchTreeResourceDataItem[];
}

export interface AssessmentParentForBatch {
  assessmentId: string;
  assessmentName: string;
  batchCode: string | null;
  batchId: string | null;
  completedStudents: number;
  failedStudents: number;
  passedStudents: number;
  pendingStudents: number;
  totalStudents: number;
}

export interface GetAssessmentsParentForBatchResponse {
  statusCode: number;
  httpStatus: string;
  response: AssessmentParentForBatch[];
}

export interface StudentAssessmentData {
  studentId: string;
  name: string;
  studentAssessmentStatus: string | null;
  status: string | null;
  totalScore: number | null;
  totalMarks: number | null;
}

export interface GetStudentsForAssessmentResponse {
  statusCode: number;
  httpStatus: string;
  response: StudentAssessmentData[];
}

export const BatchApiSlice = createApi({
  reducerPath: "batchApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAllBatches: builder.query<
      GetAllBAtchesResponse,
      {
        subjectId: string;
        pageNo: number;
        pageSize: number;
        organizationId?: string;
        userId?: string;
        search?: string;
      }
    >({
      query: ({ subjectId, pageNo, pageSize, organizationId, userId, search }) => {
        const params = new URLSearchParams({
          subjectId,
          pageNo: String(pageNo),
          pageSize: String(pageSize),
        });

        if (userId) {
          params.set("userId", userId);
        }

        if (organizationId) {
          params.set("organizationId", organizationId);
        }

        if (search) {
          params.set("search", search);
        }

        return {
          url: `batch/get-batches?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    getAllStudentsForBatch: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: GetAllStudentForBatchResponse;
      },
      { batchId: string | null; pageNo: number; pageSize: number }
    >({
      query: ({ batchId, pageNo, pageSize }) => ({
        url: `student/get?batchId=${batchId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllStudentsForElpBatch: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: StudentListforBatchResponse;
      },
      { batchId: string | null; pageNo: number; pageSize: number }
    >({
      query: ({ batchId, pageNo, pageSize }) => ({
        url: `student/get-by-batch?batchId=${batchId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllQuizForStudent: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: StudentAssessmentResponse;
      },
      {
        batchId?: string | null;
        studentId: string | undefined;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ batchId, studentId, pageNo, pageSize }) => ({
        url: `assessment/get-assessments-student?batchId=${batchId}&studentId=${studentId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllStudentsForQuiz: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: StudentAssessmentResult;
      },
      { batchId?: string | null; quizId?: string | null }
    >({
      query: ({ batchId, quizId }) => ({
        url: `assessment/get-assessment-student-result?batchId=${batchId}&assessmentId=${quizId}`,
        method: "GET",
      }),
    }),
    getStudentsQuizresponse: builder.query<
      { statusCode: number; response: QuizResponse[] },
      { studentId: string; batchId?: string | null; assessmentId: string }
    >({
      query: ({ batchId, assessmentId, studentId }) => ({
        url: `student/get-student-assessment-response?studentId=${studentId}&batchId=${batchId}&assessmentId=${assessmentId}`,
        method: "GET",
      }),
    }),
    addBatch: builder.mutation<APIResponse, { payload: AddBatchPayload }>({
      query: ({ payload }) => ({
        url: `batch`,
        method: "POST",
        body: payload,
      }),
    }),
    updateBatch: builder.mutation<
      UpdateBatchApiResponse,
      { batchId: string; payload: UpdateBatchPayload }
    >({
      query: ({ batchId, payload }) => ({
        url: `batch?batchId=${encodeURIComponent(batchId)}`,
        method: "PUT",
        body: payload,
      }),
    }),
    addBatchGSheetLink: builder.mutation<
      APIResponse,
      AddBatchGSheetLinkPayload
    >({
      query: ({ batchCode, gSheetLink }) => ({
        url: `batch/add-gSheetLink?batchCode=${encodeURIComponent(batchCode)}&gSheetLink=${encodeURIComponent(gSheetLink)}`,
        method: "PATCH",
      }),
    }),
    deactivateStudentFromBatch: builder.mutation<
      APIResponse,
      { payload: string[]; batchId: string }
    >({
      query: ({ payload, batchId }) => ({
        url: `student/deactivate-student?batchId=${batchId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getAllBatchesForStudent: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: GetBatchForStudentsResponse;
      },
      { studentId: string; pageNo: number; pageSize: number }
    >({
      query: ({ studentId, pageNo, pageSize }) => ({
        url: `batch/get-batches-student?studentId=${studentId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    joinBatchForStudent: builder.mutation<
      APIResponse,
      { batchCode: string; studentId: string }
    >({
      query: ({ studentId, batchCode }) => ({
        url: `student/join-batch?studentId=${studentId}&batchCode=${encodeURIComponent(batchCode)}`,
        method: "PATCH",
      }),
    }),
    getAllBatchesForOrg: builder.query<
      GetBatchesForOrg,
      { parentId: string; pageNo: number; pageSize: number; search?: string }
    >({
      query: ({ parentId, pageNo, pageSize, search }) => {
        const params = new URLSearchParams({
          pageNo: String(pageNo),
          pageSize: String(pageSize),
        });

        if (parentId) {
          params.set("parentId", parentId);
        }

        if (search) {
          params.set("search", search);
        }

        return {
          url: `batch/get-batches-parent?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getBatchesDropdown: builder.mutation<
      GetBatchesDropdownResponse,
      BatchesDropdown[]
    >({
      query: (payload) => ({
        url: "branch/get-batches-multiple",
        method: "POST",
        body: payload,
      }),
    }),
    getBatchById: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: GetBatchByIdResponse;
      },
      { batchId: string }
    >({
      query: ({ batchId }) => ({
        url: `batch/${batchId}`,
        method: "GET",
      }),
    }),
    getBatchStudentsElpReport: builder.query<
      GetBatchStudentsElpReportApiResponse,
      { batchId: string }
    >({
      query: ({ batchId }) => ({
        url: `batch/get-studentsStats?batchId=${batchId}`,
        method: "GET",
      }),
    }),
    invitePeopleForBatch: builder.mutation<
      InviteResponse,
      { payload: string[]; batchId: string }
    >({
      query: ({ payload, batchId }) => ({
        url: `batch/add-students?batchId=${batchId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getAssessmentNotAttendedStudents: builder.query<
      GetNotAssessmentNotAttendedStudentsResponse,
      { batchId: string; assessmentId: string }
    >({
      query: ({ batchId, assessmentId }) => ({
        url: "assessment/get-notAttended-students",
        method: "GET",
        params: { batchId, assessmentId },
      }),
    }),
    downloadAssessmentNotAttendedStudents: builder.mutation<
      FileResponse,
      { assessmentId: string; batchId: string }
    >({
      query: ({ batchId, assessmentId }) => ({
        url: "assessment/download-notAttended-students",
        method: "GET",
        params: { batchId, assessmentId },
      }),
    }),
    getBatchStudentStats: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: {
          content: any[];
          totalPages: number;
          totalElements: number;
        };
      },
      {
        batchCode: string;
        pageNo?: number;
        pageSize?: number;
        sortDirection?: string;
        sortBy?: string;
        search?: string;
      }
    >({
      query: ({
        batchCode,
        pageNo = 0,
        pageSize = 20,
        sortDirection = "ASC",
        sortBy,
        search,
      }) => {
        const params = new URLSearchParams({
          batchCode,
          pageNo: String(pageNo),
          pageSize: String(pageSize),
          sortDirection,
        });

        if (sortBy) {
          params.set("sortBy", sortBy);
        }

        if (search) {
          params.set("search", search);
        }

        return {
          url: `batch/get-student-stats?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    getResourceAccess: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: ResourceAccessResponse;
      },
      {
        resourceId: string;
        batchId: string;
        granteeId: string | string[];
        granteeType: string;
      }
    >({
      query: ({ resourceId, batchId, granteeId, granteeType }) => ({
        url: `access?resourceId=${resourceId}&batchId=${batchId}&granteeId=${granteeId}&granteeType=${granteeType}`,
        method: "GET",
      }),
    }),

    grantResourceAccess: builder.mutation<
      {
        statusCode: number;
        httpStatus: string;
        response: ResourceAccessResponse;
      },
      ResourceAccessPayload
    >({
      query: (payload) => ({
        url: `access/grant`,
        method: "POST",
        body: payload,
      }),
    }),
    getResourceData: builder.query<
      any,
      { resourceId: string; resourceType: string; batchId: string }
    >({
      query: ({ resourceId, resourceType, batchId }) => ({
        url: `batch/get-resource-data?resourceId=${resourceId}&resourceType=${resourceType}&batchId=${batchId}`,
        method: "GET",
      }),
    }),
    getStudentResourceMetaData: builder.query<
      StudentResourceMetaDataResponse,
      { batchId: string; resourceId: string }
    >({
      query: ({ batchId, resourceId }) => ({
        url: `student/get-student-resourceMetaData?batchId=${batchId}&resourceId=${resourceId}`,
        method: "GET",
      }),
    }),

    updateResourceAccess: builder.mutation<
      {
        statusCode: number;
        httpStatus: string;
        response: ResourceAccessResponse;
      },
      { resourceAccessId: string; payload: ResourceAccessPayload }
    >({
      query: ({ resourceAccessId, payload }) => ({
        url: `access?resourceAccessId=${resourceAccessId}`,
        method: "PUT",
        body: payload,
      }),
    }),

    getStudentResourceData: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: {
          content: any[];
          totalPages: number;
          totalElements: number;
        };
      },
      {
        batchId: string;
        parentId: string;
        parentType: string;
        pageNo: number;
        pageSize: number;
        studentId: string;
      }
    >({
      query: ({
        batchId,
        parentId,
        parentType,
        pageNo,
        pageSize,
        studentId,
      }) => ({
        url: `student/get-resourceData?batchId=${batchId}&parentId=${parentId}&parentType=${parentType}&pageNo=${pageNo}&pageSize=${pageSize}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    getStudentTreeResourceData: builder.query<
      GetStudentTreeResourceDataResponse,
      {
        batchId: string;
        parentId: string;
        parentType: string;
        studentId: string;
      }
    >({
      query: ({ batchId, parentId, parentType, studentId }) => ({
        url: `student/get-tree-resourceData?batchId=${batchId}&parentId=${parentId}&parentType=${parentType}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    getAssignmentResult: builder.query<
      AssignmentResultResponse,
      AssignmentResultQuery
    >({
      query: ({ batchId, parentId, parentType, studentId }) => ({
        url: "assigned-questions/assignment-result",
        method: "GET",
        params: {
          batchId,
          parentId,
          parentType,
          studentId,
        },
      }),
    }),
    getVideoResult: builder.query<
      any,
      {
        batchId: string;
        resourceId: string;
        resourceType: string;
        studentId: string;
      }
    >({
      query: ({ batchId, resourceId, resourceType, studentId }) => ({
        url: "student/video-result",
        method: "GET",
        params: {
          batchId,
          resourceId,
          resourceType,
          studentId,
        },
      }),
    }),
    getBatchTreeResourceData: builder.query<
      GetBatchTreeResourceDataResponse,
      {
        batchId: string;
        parentId: string;
        parentType: string;
      }
    >({
      query: ({ batchId, parentId, parentType }) => ({
        url: `batch/get-tree-resourceData?batchId=${batchId}&parentId=${parentId}&parentType=${parentType}`,
        method: "GET",
      }),
    }),
    getAssessmentsParentForBatch: builder.query<
      GetAssessmentsParentForBatchResponse,
      {
        batchId: string;
        parentId: string;
      }
    >({
      query: ({ batchId, parentId }) => ({
        url: `assessment/get-assessments-parent-for-batch?batchId=${batchId}&parentId=${parentId}`,
        method: "GET",
      }),
    }),
    getStudentsForAssessment: builder.query<
      GetStudentsForAssessmentResponse,
      {
        batchId: string;
        assessmentId?: string;
      }
    >({
      query: ({ batchId, assessmentId }) => {
        let URL = `assessment/get-students-assessment?batchId=${batchId}`;
        if (assessmentId) {
          URL += `&assessmentId=${assessmentId}`;
        }

        return {
          url: URL,
          method: "GET",
        };
      },
    }),
    disableBatchForStudent: builder.mutation<
      APIResponse,
      { studentId: string; status: "ACTIVE" | "INACTIVE"; batchCode: string }
    >({
      query: ({ studentId, status, batchCode }) => ({
        url: `student/disable-batch?studentId=${studentId}&status=${status}&batchCode=${batchCode}`,
        method: "PATCH",
      }),
    }),
  }),
});

export const {
  useGetAllBatchesQuery,
  useLazyGetAllBatchesQuery,
  useAddBatchMutation,
  useUpdateBatchMutation,
  useAddBatchGSheetLinkMutation,
  useGetAllStudentsForBatchQuery,
  useLazyGetAllStudentsForBatchQuery,
  useGetAllStudentsForElpBatchQuery,
  useGetAllBatchesForStudentQuery,
  useLazyGetAllBatchesForStudentQuery,
  useLazyGetAllQuizForStudentQuery,
  useLazyGetAllStudentsForQuizQuery,
  useGetStudentsQuizresponseQuery,
  useLazyGetStudentsQuizresponseQuery,
  useJoinBatchForStudentMutation,
  useGetAllBatchesForOrgQuery,
  useGetBatchesDropdownMutation,
  useLazyGetBatchByIdQuery,
  useGetBatchStudentsElpReportQuery,
  useLazyGetAllBatchesForOrgQuery,
  useDeactivateStudentFromBatchMutation,
  useInvitePeopleForBatchMutation,
  useGetAssessmentNotAttendedStudentsQuery,
  useDownloadAssessmentNotAttendedStudentsMutation,
  useGetBatchStudentStatsQuery,
  useLazyGetBatchStudentStatsQuery,
  useLazyGetResourceAccessQuery,
  useGrantResourceAccessMutation,
  useGetResourceDataQuery,
  useLazyGetResourceDataQuery,
  useGetStudentResourceDataQuery,
  useLazyGetStudentResourceDataQuery,
  useGetStudentTreeResourceDataQuery,
  useLazyGetStudentTreeResourceDataQuery,
  useGetAssignmentResultQuery,
  useLazyGetAssignmentResultQuery,
  useGetVideoResultQuery,
  useGetAssessmentsParentForBatchQuery,
  useLazyGetAssessmentsParentForBatchQuery,
  useGetStudentsForAssessmentQuery,
  useLazyGetStudentsForAssessmentQuery,
  useGetBatchTreeResourceDataQuery,
  useLazyGetBatchTreeResourceDataQuery,
  useGetStudentResourceMetaDataQuery,
  useLazyGetStudentResourceMetaDataQuery,
  useDisableBatchForStudentMutation,
  useUpdateResourceAccessMutation,
} = BatchApiSlice;
